import { Link } from 'react-router-dom';
import { Calendar, ChevronRight, MapPin, Palmtree } from 'lucide-react';
import { clsx } from '../../utils/cn.js';
import { formatMoneyCOP } from '../../utils/formatMoney.js';
import PremiumCard from '../ui/PremiumCard.jsx';
import ScrollReveal from './ScrollReveal.jsx';
import { assetUrl } from '../../utils/assetUrl.js';
import { vacacionesReglamento } from '../../content/vacacionesReglamento.js';
import publicCta from '../../content/publicCta.json';
import { getVacacionWhatsAppHref } from '../../utils/vacacionWhatsApp.js';
import SectionTopWave from './SectionTopWave.jsx';

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function VacacionesSection({
  items,
  loading,
  embedded = false,
  variant = 'default',
  waveFromDark = false,
}) {
  const list = Array.isArray(items) ? items : [];
  const isHomeLight = variant === 'green';
  const isDark = embedded && !isHomeLight;

  const ctaClass = isHomeLight
    ? 'inline-flex items-center gap-2 rounded-full border border-[#1acb12]/40 bg-white px-5 py-2.5 text-sm font-semibold text-accent-lime shadow-sm transition hover:border-[#1acb12]/70 hover:text-[#0d2b7f]'
    : isDark
      ? 'inline-flex items-center gap-2 rounded-full border border-[#1acb12]/40 bg-black/20 px-5 py-2.5 text-sm font-semibold text-accent-lime backdrop-blur-sm transition hover:border-[#1acb12]/70 hover:bg-black/30 hover:text-white'
      : 'inline-flex items-center gap-2 rounded-full border border-[#1acb12]/40 bg-white px-5 py-2.5 text-sm font-semibold text-accent-lime shadow-sm transition hover:border-[#1acb12]/70 hover:text-[#0d2b7f]';

  const content = (
    <>
        <ScrollReveal className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-accent-lime">Beneficios</p>
          <h2
            className={
              isDark
                ? 'mt-3 font-display text-3xl font-bold text-white md:text-4xl'
                : 'mt-4 font-display text-3xl font-bold text-accent-blue md:text-4xl'
            }
          >
            Sede vacacional Tocaima
          </h2>
          <p
            className={
              isDark ? 'mt-4 text-slate-400' : 'mt-5 text-lg text-slate-600'
            }
          >
            Habitaciones y casas en {vacacionesReglamento.ubicacion}. Tarifas y reservas según el reglamento vigente
            (check-in {vacacionesReglamento.horarios.checkIn}, check-out {vacacionesReglamento.horarios.checkOut}).
          </p>
        </ScrollReveal>

        <div className={`grid gap-10 lg:grid-cols-2 ${embedded ? 'mt-10' : 'mt-12'}`}>
          {loading &&
            Array.from({ length: 2 }).map((_, i) => (
              <div
                key={i}
                className={`h-96 animate-skeleton-shimmer rounded-2xl ring-1 ${isDark ? 'bg-white/15 ring-white/20' : 'bg-slate-200/90 ring-slate-200'}`}
              />
            ))}

          {!loading &&
            list.map((x, i) => {
              const waHref = getVacacionWhatsAppHref(x);

              return (
                <ScrollReveal key={x.id} delayMs={Math.min(i, 3) * 70} className="min-w-0">
                  <PremiumCard hover className="overflow-hidden !p-0">
                      <div className="grid min-w-0 gap-0 md:grid-cols-5 md:items-stretch">
                        <div className="relative flex min-h-0 min-w-0 md:col-span-2">
                          <div className="flex aspect-[4/5] w-full min-h-[14rem] items-center justify-center bg-white p-4 ring-1 ring-slate-200 md:aspect-auto md:h-full md:min-h-full">
                            {x.imagen ? (
                              <img
                                src={assetUrl(x.imagen)}
                                alt=""
                                className="mx-auto block max-h-full max-w-full object-contain object-center"
                              />
                            ) : (
                              <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center text-sm text-slate-400">
                                <Palmtree className="h-10 w-10 text-accent-lime" />
                                Sede vacacional
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="min-w-0 space-y-4 border-t border-white/5 bg-slate-950/40 p-8 md:col-span-3 md:border-l md:border-t-0">
                          <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                            <span className="inline-flex items-center gap-1 rounded-full border border-[#1acb12]/35 bg-[#1acb12]/12 px-3 py-1 text-accent-lime">
                              <MapPin className="h-3.5 w-3.5" />
                              {x.ubicacion || 'Colombia'}
                            </span>
                            <span className="inline-flex items-center gap-1 text-slate-500">
                              <Calendar className="h-3.5 w-3.5" />
                              Vigencia: {formatDate(x.fecha_inicio)} — {formatDate(x.fecha_fin)}
                            </span>
                          </div>
                          <h3 className="font-display text-2xl font-semibold text-white md:text-3xl">{x.titulo}</h3>
                          <p className="line-clamp-4 text-sm leading-relaxed text-slate-400">{x.descripcion}</p>
                          <div className="flex flex-wrap items-end justify-between gap-4 pt-4">
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Desde (por noche)
                              </p>
                              <p className="font-display text-2xl font-bold text-accent-lime md:text-3xl">
                                {Number(x.precio) > 0 ? formatMoneyCOP(x.precio) : 'Consultar'}
                              </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Link
                                to={`/sedes-vacacionales/${x.id}`}
                                className="inline-flex rounded-full border border-[#1acb12]/40 bg-[#1acb12]/10 px-5 py-2 text-sm font-semibold text-accent-lime transition hover:bg-[#1acb12]/20"
                              >
                                {publicCta.vacacionesDetailLinkLabel}
                              </Link>
                              {waHref ? (
                                <a
                                  href={waHref}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex rounded-full bg-black px-5 py-2 text-sm font-semibold text-white transition hover:bg-neutral-900"
                                >
                                  {publicCta.vacacionesCardLinkLabel}
                                </a>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>
                    </PremiumCard>
                </ScrollReveal>
              );
            })}
        </div>

        {!loading && list.length > 0 && (
          <ScrollReveal className="mt-10 flex flex-col items-center gap-4 text-center sm:flex-row sm:justify-center">
            <Link to="/sedes-vacacionales" className={ctaClass}>
              {publicCta.vacacionesSeeAllLabel}
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Link>
            <Link
              to="/sedes-vacacionales#reglamento"
              className={
                isHomeLight
                  ? 'text-sm font-semibold text-accent-lime underline-offset-4 hover:text-[#0d2b7f] hover:underline'
                  : isDark
                    ? 'text-sm font-semibold text-accent-lime underline-offset-4 hover:text-white hover:underline'
                    : 'text-sm font-semibold text-accent-lime underline-offset-4 hover:text-[#0d2b7f] hover:underline'
              }
            >
              Ver reglamento y tarifas
            </Link>
          </ScrollReveal>
        )}

        {!loading && list.length === 0 && (
          <p
            className={`mt-10 text-center ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
          >
            No hay sedes vacacionales publicadas por ahora.
          </p>
        )}
    </>
  );

  if (embedded && !isHomeLight) {
    return <div id="sedes-vacacionales">{content}</div>;
  }

  return (
    <section
      id="sedes-vacacionales"
      className={clsx(
        'relative isolate scroll-mt-24 overflow-hidden py-16 lg:py-24',
        isHomeLight
          ? 'border-t-0 bg-white'
          : 'border-t border-slate-200 bg-slate-50',
        waveFromDark && isHomeLight && 'pt-14 lg:pt-16'
      )}
    >
      {isHomeLight && (
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-[#1acb12] to-transparent"
          aria-hidden
        />
      )}
      {waveFromDark && isHomeLight && <SectionTopWave />}
      <div className="relative z-[2] mx-auto max-w-7xl px-6 lg:px-8">{content}</div>
    </section>
  );
}
